"use client";
import React, { FC, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import ButtonPrimary from "@/shared/ButtonPrimary";
import ButtonSecondary from "@/shared/ButtonSecondary";
import { Route } from "@/routers/types";
import { useAppSelector, useAppDispatch } from "@/app/lib/hooks";
import { RootState } from "@/app/lib/store";
import { register, removeAuthError, logout } from "@/app/lib/reducers/authReducer";
import {
  updateAnswers,
  fetchRecruitmentInfo,
  ensureRecruitment,
  updateRecruitmentStatus,
  updateUserStatus,
  updateGuidePreferences,
  uploadFile,
} from "@/app/lib/reducers/recruitmentReducer";
import { sendUserSiteRequest } from "@/app/lib/siteRequests";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/navigation";

// "CARNET DE VOYAGE" newsletter — same component the account/contact pages
// use. Lazy + SSR-off (below the fold). Presentational only.
const SectionNewsletter = dynamic(
  () => import("../../(home)/desktop/SectionNewsletter"),
  {
    ssr: false,
    loading: () => <div style={{ minHeight: 220 }} aria-hidden="true" />,
  }
);

export interface CommonLayoutProps {
  children: React.ReactNode;
  params: {
    stepIndex: string;
  };
}

const WAV_EXT = ".wav";
const toSafeBase = (key: string) =>
  key.toLowerCase().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").slice(0, 60);
const toWavName = (key: string) => `${toSafeBase(key)}${WAV_EXT}`;
const dataURLToBlob = (dataURL: string): Blob => {
  const [head, body] = dataURL.split(",");
  const mime = head.match(/:(.*?);/)?.[1] || "audio/wav";
  const bin = atob(body || "");
  const len = bin.length;
  const u8 = new Uint8Array(len);
  for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
  return new Blob([u8], { type: mime });
};

const CommonLayout: FC<CommonLayoutProps> = ({ children, params }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("guydeez");
  const router = useRouter();
  const auth = useAppSelector((state: RootState) => state.auth);
  const u = useAppSelector((state: RootState) => state.auth.user);
  const r = useAppSelector((state: RootState) => state.recruitment.formData);
  const cmsEntry = useAppSelector((state: RootState) => state.cmsEntryData.cmsEntryData);
  const answers = useAppSelector((state: RootState) => state.recruitment.cmsAnswers);
  const preferences = useAppSelector((state: RootState) => state.recruitment.guidePreferences);
  const audio = useAppSelector((state: RootState) => state.recruitment.audioRecordings);
  const uploadFileLoading = useAppSelector((state: RootState) => state.recruitment.uploadFileLoading);

  const [btnContinue, setBtnContinue] = useState(false);
  const [isUploadingAudios, setIsUploadingAudios] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const index = Number(params.stepIndex) || 1;
  const REQUIRED_AUDIO_COUNT = 5;

  const nextHref = (index < 7 ? `/recruitment/${index + 1}` : `/recruitment/1`) as Route;
  const backtHref = (index > 1 ? `/recruitment/${index - 1}` : `/recruitment/1`) as Route;
  const nextBtnText = index > 5 ? t('recruitment_send_all_data') : t('continue');

  const nbQuestions = cmsEntry?.length;
  const questionsFilled = Object.values(answers).filter(
    (value): value is string => typeof value === "string" && value.trim() !== ""
  ).length;
  const isEmailValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email) &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.confirmEmail);

  type AudioValue = Blob | string | undefined | null;
  const audioRecordingsMap = audio as Record<string, AudioValue>;
  const hasAllAudioRecordings =
    Object.values(audioRecordingsMap).filter(Boolean).length >= REQUIRED_AUDIO_COUNT;

  const selectedLangFromStore = useAppSelector((state) => state.language.selectedLanguage);
  const selectedLanguage = selectedLangFromStore || "en_US";

  useEffect(() => {
    if (u?.uuid) {
      // GET only — register() creates the recruitment record at signup, so
      // we just load it here (matches the pre-redesign behaviour). Creating
      // it here too would race register()'s createRecruitment and produce a
      // duplicate 500. The audio step keeps a create-if-missing safety net.
      dispatch(fetchRecruitmentInfo(u.uuid));
    }
    if (index === 3 && u?.uuid) {
      dispatch(updateRecruitmentStatus(u.uuid, "2/7"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, u, index]);

  // Arriving on the guide signup (step 1) while logged in: the form always
  // creates a brand-new guide account, so a stale session would be both
  // confusing and would route the register through its "already-logged-in"
  // branch. Log non-guides out for a clean slate. Guides (role 3) keep
  // their session — that's the resume-an-in-progress-application flow.
  useEffect(() => {
    if (index !== 1 || !u) return;
    if (u.role === 3) return;
    dispatch(logout());
  }, [index, u, dispatch]);

  useEffect(() => {
    if (
      r.email !== "" &&
      r.email === r.confirmEmail &&
      isEmailValid &&
      r.password !== "" &&
      r.password === r.confirmPassword &&
      r.city !== "-1" &&
      r.language !== -1 &&
      r.lastName !== "" &&
      r.firstName !== "" &&
      r.phone !== "" &&
      r.birthdate !== "" &&
      r.termsAccepted
    ) {
      setBtnContinue(true);
    } else {
      setBtnContinue(false);
    }
  }, [r, isEmailValid]);

  useEffect(() => {
    let canContinue = false;

    if (index === 1) canContinue = true;
    if (
      index === 2 &&
      r.email !== "" &&
      r.email === r.confirmEmail &&
      r.password !== "" &&
      r.password === r.confirmPassword &&
      r.city !== "-1" &&
      r.language !== -1 &&
      r.lastName !== "" &&
      r.firstName !== "" &&
      r.phone !== "" &&
      r.birthdate !== "" &&
      r.termsAccepted
    ) {
      canContinue = true;
    }
    if (index === 2 && u?.role === 3 && u?.status === "guide_fill_form_in_progress") {
      canContinue = true;
    }
    if ([4, 5].includes(index)) {
      canContinue = true;
    }
    if (index === 6) {
      canContinue = hasAllAudioRecordings && !uploadFileLoading && !isUploadingAudios;
    }
    setBtnContinue(canContinue);

    if (index === 7 && u?.uuid) {
      dispatch(updateRecruitmentStatus(u.uuid, "7/7"));
      dispatch(updateUserStatus(u.uuid, "ready_to_be_reviewed"));
      // Accuse de reception au candidat (modele CMS), a l'adresse de son compte ;
      // le backend n'en envoie qu'un par jour si cette etape est rejouee.
      sendUserSiteRequest({ kind: "guide-application" }, selectedLanguage).catch(() => {});
    }
  }, [index, u, r, selectedLanguage, dispatch, hasAllAudioRecordings, uploadFileLoading, isUploadingAudios, isEmailValid]);

  useEffect(() => {
    if (index === 3) {
      setBtnContinue(questionsFilled === nbQuestions);
    }
  }, [answers, nbQuestions, index]);

  useEffect(() => {
    if (auth?.error === "An unknown error occurred during registration") {
      setErrorMessage(
        t('recruitment_error_account_not_created')
      );
    }
  }, [auth]);

  const uploadAllAudioRecordings = async () => {
    if (!u?.uuid) return;

    const recMap = audio as Record<string, AudioValue>;

    for (const [questionKey, rawVal] of Object.entries(recMap)) {
      if (!rawVal) continue;

      let fileBlob: Blob | null = null;
      if (rawVal instanceof Blob) {
        fileBlob = rawVal;
      } else if (typeof rawVal === "string" && rawVal.startsWith("data:")) {
        fileBlob = dataURLToBlob(rawVal);
      }

      if (!fileBlob) continue;

      const wavBlob = fileBlob.type ? fileBlob : new Blob([fileBlob], { type: "audio/wav" });

      const formData = new FormData();
      formData.append("path", `${u.uuid}/`);
      formData.append("file", wavBlob, `${questionKey}.wav`);
      formData.append("bucket", "guydeez-recruitment");
      formData.set("name", questionKey);
      formData.set("type", "audio/wav");
      formData.set("extension", WAV_EXT.replace(".", "")); // "wav"
      formData.append("uuid", u.uuid);

      const result = await dispatch(uploadFile(formData) as unknown as Promise<{ success?: boolean; error?: string }>);
      if (!result?.success) {
        throw new Error(result?.error || "Upload failed");
      }
    }
  };

  const informationMissing = () => {
    const missingFields: string[] = [];
    if (r.email === "") missingFields.push(t('checkout_email'));
    if (!isEmailValid) missingFields.push(t('recruitment_email_is_not_valid'));
    if ((r.email !== "" || r.confirmEmail !== "") && r.email !== r.confirmEmail)
      missingFields.push(t('recruitment_emails_not_equal'));
    if (r.password === "") missingFields.push(t('password'));
    if ((r.password !== "" || r.confirmPassword !== "") && r.password !== r.confirmPassword)
      missingFields.push(t('recruitment_passwords_not_equal'));
    if (r.city === "-1") missingFields.push(t('city'));
    if (r.language === -1) missingFields.push(t('language'));
    if (r.lastName === "") missingFields.push(t('account_last_name'));
    if (r.firstName === "") missingFields.push(t('account_first_name'));
    if (r.birthdate === "") missingFields.push(t('account_date_of_birth'));
    if (r.phone === "") missingFields.push(t('account_phone_number'));
    if (!r.termsAccepted) missingFields.push(t('recruitment_accept_terms'));

    if (missingFields.length > 0 && index === 2) {
      setErrorMessage(`${t('recruitment_error_required_fields_missing')}: ${missingFields.join(", ")}.`);
    } else {
      setErrorMessage("");
      updateDataForEachPage();
    }

    if (questionsFilled !== nbQuestions && index === 3) {
      setErrorMessage(
        t('recruitment_error_answer_all_questions')
      );
    }

    if (index === 6 && !hasAllAudioRecordings) {
      setErrorMessage(t('recruitment_error_answer_all_questions'));
    }
  };

  const backToPreviousPage = () => {
    setErrorMessage("");
    dispatch(removeAuthError());
  };

  const updateDataForEachPage = () => {
    if (index === 2) {
      if (!u) {
        // No user yet: create account with the data from step 2
        dispatch(register(r, () => {}));
      } else if (u.role !== 3 && u.status !== "guide_fill_form_in_progress") {
        // Logged-in non-guide finishing registration flow
        dispatch(register(r, () => {}));
      } else if (u.role === 3 && u.status === "guide_fill_form_in_progress") {
        // Guide already in progress: allow continue
        setBtnContinue(true);
      }
    } else if (index === 3 && u?.uuid) {
      dispatch(updateAnswers(u.uuid, answers));
      dispatch(updateRecruitmentStatus(u.uuid, "3/7"));
    } else if (index === 4 && u?.uuid) {
      dispatch(updateGuidePreferences(u.uuid, preferences));
      dispatch(updateRecruitmentStatus(u.uuid, "4/7"));
    } else if (index === 5 && u?.uuid) {
      dispatch(updateRecruitmentStatus(u.uuid, "5/7"));
    }
  };

  const handleSendAllData = async () => {
    if (!u?.uuid) return;
    if (!hasAllAudioRecordings) {
      setErrorMessage(t('recruitment_error_answer_all_questions'));
      return;
    }

    setErrorMessage("");
    setIsUploadingAudios(true);
    try {
      // Guarantee the recruitment record exists RIGHT before the audio
      // PUTs — otherwise they 404 ("No recruitment information found").
      await dispatch(ensureRecruitment(u.uuid, r.bikeTour, r.carTour));
      await uploadAllAudioRecordings();
      await dispatch(updateRecruitmentStatus(u.uuid, "6/7"));
      router.push(nextHref);
    } catch (error) {
      console.error("Failed to upload all audio answers", error);
      setErrorMessage(
        t("recruitment_error_audio_upload_failed", {
          defaultValue:
            "Impossible d'envoyer vos réponses audio. Veuillez réessayer.",
        }) as string
      );
    } finally {
      setIsUploadingAudios(false);
    }
  };

  // Step 2 = account creation. We must NOT advance to step 3 unless the
  // account is actually created — otherwise the user lands on step 3
  // "logged out" (auth.user stays null) and every later authenticated
  // call (photo/audio upload) 401s. So: navigate ONLY from register's
  // success callback. On failure, register dispatches registerFailure +
  // a toast and we stay on step 2.
  const handleStep2Continue = () => {
    // Guide already mid-form (came back later): no re-register, just go on.
    if (u && u.role === 3 && u.status === "guide_fill_form_in_progress") {
      router.push(nextHref);
      return;
    }
    // Hard-stop on a missing/invalid birthdate BEFORE creating the account:
    // an empty date reaches Postgres as "" and 500s ("invalid input syntax
    // for type date"). Show a clear, specific message instead of a generic
    // server error.
    const dob = (r.birthdate || "").trim();
    if (!dob || Number.isNaN(Date.parse(dob))) {
      setErrorMessage(
        t("recruitment_error_birthdate_required", {
          defaultValue: "Veuillez renseigner votre date de naissance.",
        }) as string
      );
      return;
    }
    setErrorMessage("");
    dispatch(removeAuthError());
    // CRITICAL: do NOT navigate from inside register()'s callback. That
    // callback fires mid-thunk (right after REGISTER_SUCCESS) and a
    // router.push() there throws/aborts the thunk BEFORE its createRecruitment
    // + languageByUser + guideByCity calls run — leaving guides with no
    // recruitment record, no languages, no city. Instead: let register()
    // finish ALL its work (the callback only flips a success flag), then
    // navigate once it resolves.
    let registered = false;
    Promise.resolve(
      dispatch(register(r, () => {
        registered = true;
      }) as any)
    ).then(() => {
      if (registered) router.push(nextHref);
    });
  };

  const handleContinue = async (e?: React.MouseEvent) => {
    if (index === 6) {
      e?.preventDefault();
      await handleSendAllData();
      return;
    }
    if (index === 2) {
      e?.preventDefault();
      handleStep2Continue();
      return;
    }

    updateDataForEachPage();
  };

  // ---- Presentational stepper labels (7 steps). i18n with FR defaults so
  // nothing breaks if a key is missing. Order/numbering is fixed by the
  // wizard logic above — this only renders it. ----
  const STEPS: Array<{ title: string; sub: string }> = [
    {
      title: t("recruitment_step1_title", { defaultValue: "Bienvenue" }) as string,
      sub: t("recruitment_step1_sub", { defaultValue: "Premier contact" }) as string,
    },
    {
      title: t("recruitment_step2_title", { defaultValue: "Entretien" }) as string,
      sub: t("recruitment_step2_sub", { defaultValue: "Vos coordonnées" }) as string,
    },
    {
      title: t("recruitment_step3_title", { defaultValue: "Quelques questions sur vous" }) as string,
      sub: t("recruitment_step3_sub", {
        defaultValue: "Vos réponses",
      }) as string,
    },
    {
      title: t("recruitment_step4_title", { defaultValue: "Profil guide" }) as string,
      sub: t("recruitment_step4_sub", { defaultValue: "Votre approche" }) as string,
    },
    {
      title: t("recruitment_step5_title", { defaultValue: "Photo" }) as string,
      sub: t("recruitment_step5_sub", { defaultValue: "On veut vous voir" }) as string,
    },
    {
      title: t("recruitment_step6_title", { defaultValue: "Audio" }) as string,
      sub: t("recruitment_step6_sub", { defaultValue: "Racontez-nous" }) as string,
    },
    {
      title: t("recruitment_step7_title", { defaultValue: "Récap" }) as string,
      sub: t("recruitment_step7_sub", { defaultValue: "Validation" }) as string,
    },
  ];

  return (
    <div className="nc-PageAddListing1" style={{ background: "var(--g-cream)" }}>
      {/* ===== MOBILE chrome (lg:hidden): breadcrumb + compact progress ===== */}
      <div className="lg:hidden">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="gdx-scroll-x"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "10px 18px",
            fontSize: 12,
            background: "var(--g-cream)",
            borderBottom: "1px solid var(--g-line)",
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "var(--g-ink-500)", fontWeight: 500 }}>
            {t("breadcrumb_work_with_us", { defaultValue: "Travaillez avec nous" })}
          </span>
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--g-ink-500)"
            strokeWidth="2"
            style={{ flexShrink: 0, opacity: 0.5 }}
            aria-hidden="true"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
          <span style={{ color: "var(--g-burgundy)", fontWeight: 700 }}>
            {t("become_a_guide", { defaultValue: "Devenir guide" })}
          </span>
        </nav>

        {/* Progress: N/7 + current step name + 7-segment bar */}
        <div
          style={{
            padding: "14px 18px",
            background: "var(--g-cream)",
            borderBottom: "1px solid var(--g-line)",
          }}
        >
          <div
            className="flex justify-between"
            style={{ alignItems: "baseline", marginBottom: 10 }}
          >
            <div className="flex" style={{ alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: "var(--g-ink-900, #1a1a1a)" }}>
                {index}
              </span>
              <span style={{ fontSize: 14, color: "var(--g-ink-500)", fontWeight: 600 }}>
                / 7
              </span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--g-burgundy)" }}>
              {STEPS[Math.min(Math.max(index, 1), 7) - 1]?.title}
            </span>
          </div>
          <div className="flex" style={{ gap: 4 }}>
            {STEPS.map((_, i) => (
              <span
                key={i}
                style={{
                  flex: 1,
                  height: 5,
                  borderRadius: 99,
                  background:
                    i + 1 < index
                      ? "var(--g-green-ada, #1F7A45)"
                      : i + 1 === index
                        ? "var(--g-burgundy)"
                        : "var(--g-line)",
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div
        className="max-w-[1320px] mx-auto"
        style={{ padding: "28px 24px 48px" }}
      >
        {/* ---- Stepper (desktop only; mobile uses the compact bar above) ---- */}
        <div
          className="gdx-scroll-x hidden lg:flex"
          style={{
            alignItems: "flex-start",
            overflowX: "auto",
            paddingBottom: 8,
            marginBottom: 28,
          }}
        >
          {STEPS.map((s, i) => {
            const n = i + 1;
            const completed = n < index;
            const active = n === index;
            const circleBg = active
              ? "var(--g-burgundy)"
              : completed
                ? "var(--g-green-ada, #1F7A45)"
                : "white";
            const circleColor = active || completed ? "white" : "var(--g-ink-500)";
            const circleBorder = active
              ? "var(--g-burgundy)"
              : completed
                ? "var(--g-green-ada, #1F7A45)"
                : "var(--g-line)";
            return (
              <React.Fragment key={n}>
                <div
                  className="flex items-center"
                  style={{ gap: 10, flexShrink: 0 }}
                >
                  <span
                    className="grid place-items-center"
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: circleBg,
                      color: circleColor,
                      border: `1.5px solid ${circleBorder}`,
                      fontSize: 13,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {completed ? "✓" : n}
                  </span>
                  <span style={{ whiteSpace: "nowrap" }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 700,
                        color: active
                          ? "var(--g-burgundy)"
                          : "var(--g-ink-900, #1a1a1a)",
                        lineHeight: 1.2,
                      }}
                    >
                      {s.title}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 11,
                        color: "var(--g-ink-500)",
                        lineHeight: 1.2,
                        marginTop: 2,
                      }}
                    >
                      {s.sub}
                    </span>
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <span
                    aria-hidden="true"
                    style={{
                      flex: 1,
                      minWidth: 24,
                      height: 2,
                      background: completed
                        ? "var(--g-green-ada, #1F7A45)"
                        : "var(--g-line)",
                      margin: "15px 12px 0",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ---- Step counter (desktop only; mobile shows it in the bar above) ---- */}
        <div className="hidden lg:block" style={{ marginBottom: 16 }}>
          <span
            style={{
              fontSize: 34,
              fontWeight: 800,
              color: "var(--g-burgundy)",
            }}
          >
            {index}
          </span>{" "}
          <span style={{ fontSize: 16, color: "var(--g-ink-500)" }}>/ 7</span>
        </div>

        {/* ---- Step content card ---- */}
        <div
          className="gdx-card"
          style={{
            padding: "clamp(20px, 4vw, 36px)",
            background: "white",
            borderColor: "transparent",
            boxShadow: "var(--g-shadow-sm, 0 2px 6px rgba(0,0,0,.04))",
          }}
        >
          {children}
        </div>

        {errorMessage && (
          <div
            role="alert"
            style={{
              marginTop: 18,
              padding: 14,
              borderRadius: 10,
              background: "rgba(193,59,92,.08)",
              border: "1px solid rgba(193,59,92,.25)",
              color: "var(--g-coral, #C13B5C)",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {errorMessage}
          </div>
        )}

        {index <= 6 && (
          <div
            className="flex flex-row items-center lg:justify-end"
            style={{ gap: 14, marginTop: 24 }}
          >
            {index > 1 && (
              <ButtonSecondary
                href={backtHref}
                onClick={backToPreviousPage}
                className="flex-shrink-0"
              >
                {t("go_back")}
              </ButtonSecondary>
            )}
            <ButtonPrimary
              // Step 2 navigates from register's success callback (not via
              // the Link href), so a failed account creation can't advance.
              href={
                index !== 6 && index !== 2 && btnContinue ? nextHref : undefined
              }
              onClick={btnContinue ? handleContinue : informationMissing}
              disabled={
                (index === 6 && !btnContinue) || (index === 2 && auth.loading)
              }
              loading={
                (index === 6 && isUploadingAudios) ||
                (index === 2 && auth.loading)
              }
              // Same row as "Retour": the primary grows to fill the line on
              // mobile (full-width when alone on step 1), auto width on desktop.
              className={`flex-1 lg:flex-none ${
                index === 6 && !btnContinue ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {nextBtnText || t("continue")}
            </ButtonPrimary>
          </div>
        )}
      </div>

      {/* ---- "CARNET DE VOYAGE" newsletter ---- */}
      <SectionNewsletter />
    </div>
  );
};

export default CommonLayout;
